import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import { generateClient } from '@aws-amplify/api';
import { getProtocols } from '@/graphql/queries';
import { upsertProtocol } from '@/graphql/mutations';
import useBodyClick from '@/hooks/useBodyClick';
import useArrowKeys from '@/hooks/useArrowKeys';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import Header from '@/Header';
import { dash, downIcon, SpinnerLineDark, upIcon } from '@/utils';
import { getListSort, setListSort } from '@/utils/stickySettings';

const client = generateClient();

export default () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  const updateBusy = useCallback((id, value) => {
    setBusy(prevState => ({ ...prevState, [id]: value }));
  });

  const [search, setSearch] = useState('');
  const [searchCount, setSearchCount] = useState(0);
  const incSearchCount = useCallback(() => setSearchCount((p) => p + 1), []);
  const debounceSearch = useCallback(_.debounce((v) => { setDefaults(); setSearch(v) }, 500), []);

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(() => getListSort({ key: window.location.pathname }) || { field: 'updated_at', order: 'desc' }, []);
	const [sortField, setSortField] = useState(defaultSortField && defaultSortOrder ? defaultSortField : undefined);
	const [sortOrder, setSortOrder] = useState(defaultSortField && defaultSortOrder ? { [defaultSortField]: defaultSortOrder } : {});

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const filters = [];
    const q = (search || '').trim();
    if (q) {
      const n = Number(q);
      filters.push(
        ...(!isNaN(n) ? [{ id: { eq: n } }] : []),
        { protocol_name: { like: `%${q}%` } }
      );
    }
    const sorting = [
      { field: sortField, order: sortOrder[sortField] },
      // Always add id as a tie-breaker to avoid indeterminate order
      { field: 'id', order: 'asc' }
    ];
    return {
      filters,
      sorting
    };
  }, [search, sortField, sortOrder]);

  const { isLoadingMore, limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal } = useLoadMore({ position: 'absolute' });
  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'protocols', setRows });

  // ---------- state ----------
  const [sidebarItem, _setSidebarItem] = useState();
  const setSidebarItem = useCallback(item => {
    _setSidebarItem(item);
    const queryParams = new URLSearchParams(window.location.search);
    if (!item) {
      queryParams.delete('protocol_id');
    } else {
      queryParams.set('protocol_id', item.id);
    }
    navigate({ pathname: window.location.pathname, search: `?${queryParams.toString()}` }, { replace: true });
  }, []);

  const [loadCount, setLoadCount] = useState(0);
  const incLoadCount = () => setLoadCount(prevState => prevState + 1);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const protocol_id = parseInt(queryParams.get('protocol_id') || undefined, 10);
    if (protocol_id) {
      const item = rows.find(e => e.id === protocol_id);
      if (item) setSidebarItem(item);
    }
  }, [rows]);

  // ---------- load from GraphQL ----------
  const load = useCallback(async () => {
    const { filters, sorting } = filters_ref.current;
    const res = await client.graphql({
      query: getProtocols,
      variables: { filters, limit, offset, sorting }
    });

    const payload = res?.data?.getProtocols;
    const data = payload?.protocols ?? [];
    setRows(prevState => {
      const _data = (offset === 0 ? data : [...prevState, ...data]);
      return _data;
    });
    setTotal(payload?.total_count ?? data.length);
    incLoadCount();
    setIsLoadingMore(false);
  }, [filters_ref.current, limit, offset]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({ rows, setSidebarRow: setSidebarItem, sidebarRow: sidebarItem });

  // ---------- actions ----------
  const handleDelete = useCallback(async (row) => {
    const ok = window.confirm(`Delete protocol "${row.protocol_name ?? row.id}"?`);
    if (!ok) return;
    try {
      updateBusy(row.id, true);
      if (row.id === sidebarItem?.id) {
        setSidebarItem();
      }
      await client.graphql({
        query: upsertProtocol,
        variables: {
          input: { protocols: { id: row.id } },
          input_delete: [{ id: row.id, key: 'protocols' }],
        },
      });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      console.error(e);
      alert('Delete failed');
    } finally {
      updateBusy(row.id, false);
    }
  }, [sidebarItem]);

  const handleSort = field => () => {
    if (sortField === field) {
      setSortOrder(prevState => {
        const order = prevState[sortField] === 'asc' ? 'desc' : 'asc';
        setListSort({ field, key: window.location.pathname, order });
        return ({ ...prevState, [field]: order })
      });
    } else {
      setSortOrder(prevState => {
        const order = prevState[field] || 'asc';
        setListSort({ field, key: window.location.pathname, order });
        return ({ ...prevState, [field]: order })
      });
      setSortField(field);
    }
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === 'asc' ? upIcon : downIcon;

  // ---------- table helpers  ----------
  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#F4F4F4",
    zIndex: 2,
    fontSize: "0.875rem",
    textAlign: "left",
  };

  const idCellStyle        = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 90  };
  const textCellStyle      = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 250 };            
  const lastCellStyle      = { padding: "0 12px", maxWidth: 240 };                                

  const protocol_breakout = protocol => {
    // protocol_extract_json -> 
    let protocol_name = "";
    try {
      if (protocol?.protocol_extract_json) {
        const json = JSON.parse(protocol.protocol_extract_json);
        protocol_name = json?.filename || "";
      }
    } catch (_) {}
    return (
      <div onClick={event => event.stopPropagation()}>
        <Link
          to={`/app/customers/protocols/${protocol.id}`}
          className="text-decoration-none fg-purple"
         >
          <h5 className="mb-3 fg-purple text-break"><b>{protocol.protocol_name}</b></h5>
        </Link>
        <div className="row g-0 mb-2">
          <div className="ms-auto col-auto"><u><b>Count</b></u></div>
        </div>
        <Link
          className="text-decoration-none"
          style={{ color: "inherit" }}
          to={`/app/customers/protocols/subjects?protocol_id=${protocol.id}&protocol_name=${encodeURIComponent(
            protocol.protocol_name || ""
          )}`}
        >
          <div className="row g-0 mb-2">
            <div className="col">Protocol Matches</div>
            <div className="col-auto">{protocol.subject_count}</div>
          </div>
        </Link>
      </div>
    );
  };

  const table = useMemo(() => (
    <div className="row g-0">
      <div className="col pe-0">
        {/* Table */}
        <div className="border rounded-3 overflow-auto" style={{ maxHeight: `calc(100vh - 150px)` }}>
          <table
            className="table table-sm table-borderless align-middle mb-0"
            onClick={event => event.stopPropagation()}
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", tableLayout: "auto" }}
          >
            <thead className="text-muted small" style={{ height: "30px" }}>
              <tr className="border-bottom">
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('id')}>ID{sortField === 'id' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('protocol_name')}>Protocol Name{sortField === 'protocol_name' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('updated_at')}>Updated At{sortField === 'updated_at' && sortIcon}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                return (
                  <tr
                    className={`position-relative cursor-default border-bottom ${sidebarItem?.id === r.id ? 'bw-focus-ring' : ''}`}
                    key={r.id}
                    onClick={event => { event.stopPropagation(); setSidebarItem(r); }}
                    style={{ height: "30px" }}
                  >
                    {/* ID */}
                    <td className="text-start align-middle py-2 ps-3" style={idCellStyle}>
                      <Link
                        to={`/app/customers/studies/${r.id}`}
                        className="text-decoration-none"
                        style={{ color: "inherit" }}
                      >
                        {r.id}
                        {busy[r.id] ? (
                          <SpinnerLineDark />
                        ) : undefined}
                      </Link>
                    </td>

                    {/* Protocol Name */}
                    <td
                      className="text-start align-middle py-2 ps-3 text-break"
                      style={textCellStyle}
                      title={r.protocol_name || ""}
                    >
                      <Link
                        to={`/app/customers/protocols/${r.id}`}
                        className="text-decoration-none fg-purple"
                      >
                        <b>{r.protocol_name || dash}</b>
                      </Link>
                    </td>

                    {/* Updated At + Actions  */}
                    <td className="align-middle py-2 ps-3" style={lastCellStyle}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          {r.updated_at ? moment(r.updated_at).fromNow() : "—"}
                        </span>

                        <div className="d-flex align-items-center">
                          <Link style={{ color: "var(--bs-body-color)" }} to={`/app/customers/protocols/${r.id}`}>
                            <FontAwesomeIcon
                              className="cursor-pointer me-3"
                              icon={icons.faPenToSquare}
                              title="Edit"
                            />
                          </Link>

                          <FontAwesomeIcon
                            className="cursor-pointer text-danger"
                            icon={icons.faTrashCan}
                            onClick={event => { event.stopPropagation(); handleDelete(r); }}
                            title="Delete"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {sidebarItem && (
        <div className="col-auto ps-4 width_300">
          <div className="position-sticky" style={{maxHeight: '100vh', overflow: 'auto', top: '0px'}}>
            <button aria-label="Close" className="btn-close float-end" onClick={() => setSidebarItem()} type="button"></button>
              {protocol_breakout(sidebarItem)}
          </div>
        </div>
      )}
    </div>
  ), [loadCount, sidebarItem, isLoadingMore, JSON.stringify(busy), rows]);

  return (
    <div className="container-fluid px-4">
      <Header path="/app/customers/">
        {/* Header */}
        <div className="row align-items-center mt-3 mb-3 g-0">
          <div className="col">
            <h4 className="mb-0 ps-4"  style={{ color: '#2E2868' }}>Protocols</h4>
          </div>
          {/* Search Box */}
          <div className="col-auto ms-auto pe-0 position-relative">
            <input
              autoFocus
              className="form-control width_200"
              defaultValue={search}
              key={searchCount}
              placeholder="Search..."
              onChange={(e) => debounceSearch(e.target.value)}
            ></input>
            <i
              className="bi bi-x position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
              onClick={() => {
                setSearch("");
                incSearchCount();
              }}
              role="button"
            ></i>
          </div>

          {/* Create New Protocol Button */}
          <div className="col-auto">
            <Link to="/app/customers/protocols/new">
              <button
                className="btn btn-primary"
                style={{
                  backgroundColor: "#2E2868",
                  borderColor: "#2E2868",
                  marginLeft: "20px",
                }}
              >
                Create New Protocol
              </button>
            </Link>
          </div>
        </div>
        <hr />
      </Header>

      {rows?.length ? (
        <AnimatePresence initial={true}>
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1.0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {table}
      			{loadMoreButton}
          </motion.div>
        </AnimatePresence>
      ) : undefined}

    </div>
  );
};
